import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { Seo } from "../components/Seo";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";

const SECTIONS = [["where_you_are", "Where You Are"], ["where_you_want_to_go", "Where You Want To Go"], ["whats_working", "What Is Working"], ["holding_back", "What Is Holding You Back"], ["change_first", "What Needs To Change First"]];
const HUGH_KEYS = [["highlight", "H — HIGHLIGHT"], ["upgrade", "U — UPGRADE"], ["generate", "G — GENERATE"], ["harvest", "H — HARVEST"]];

export default function Prescription() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/acquisition/prescription/${token}`).then((r) => setData(r.data)).catch(() => setErr(true)); }, [token]);

  if (err) return <div className="flex min-h-screen items-center justify-center bg-[#0A0C0F] px-6 text-center text-white"><p className="text-[#8A939B]">This prescription isn't available.</p></div>;
  if (!data) return <div className="flex min-h-screen items-center justify-center bg-[#0A0C0F] text-[#8A939B]">Loading…</div>;
  const rx = data.prescription;

  const accept = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/acquisition/prescription/${token}/accept`);
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0C0F] text-white">
      <Seo title="Your HughLaunch Prescription™" description="Your personalized H.U.G.H. growth plan." path={`/prescription/${token}`} noindex />
      <div className="mx-auto max-w-3xl space-y-5 px-5 py-12">
        <a href="/" className="inline-flex items-center gap-2"><img src="/hl-logo-v2.png" alt="HughLaunch" className="h-8 w-auto" /><span className="text-lg font-extrabold">Hugh<span className="text-[#C6FF00]">Launch</span></span></a>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C6FF00]">HughLaunch Prescription™ — {data.business_name}</p>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-4xl">Your plan to close the gap.</h1>
          <p className="mt-2 text-sm text-[#8A939B]">Built from your Brand Score ({data.score}/100 — {data.classification}), your audit, and your Audit Review Call.</p>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/acquisition/prescription-pdf/${token}`} target="_blank" rel="noreferrer" data-testid="rx-download-pdf"
            className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-[#2A2D34] px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#8A939B] transition-colors hover:border-[#C6FF00] hover:text-[#C6FF00]">
            Download as PDF <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </div>
        {SECTIONS.map(([k, label]) => rx[k] ? (
          <div key={k} className="rounded-2xl border border-[#1c1f25] bg-[#101216] p-6" data-testid={`rx-${k}`}>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#C6FF00]">{label}</p>
            <p className="mt-3 whitespace-pre-line text-[15px] leading-relaxed text-[#c8cdd3]">{rx[k]}</p>
          </div>
        ) : null)}
        <div className="rounded-2xl border border-[#1c1f25] bg-[#101216] p-6" data-testid="rx-hugh-plan">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#C6FF00]">The H.U.G.H. Plan</p>
          <div className="mt-4 space-y-4">
            {HUGH_KEYS.map(([k, label]) => rx.hugh_plan?.[k] ? (
              <div key={k} className="rounded-xl border border-[#23262c] bg-[#14161b] p-4">
                <p className="font-extrabold text-white">{label}</p>
                <p className="mt-1 whitespace-pre-line text-sm text-[#8A939B]">{rx.hugh_plan[k]}</p>
              </div>
            ) : null)}
          </div>
        </div>
        {data.plan && (
          <div className="rounded-2xl border border-[#C6FF00]/40 bg-[#C6FF00]/[0.06] p-6" data-testid="rx-recommendation">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#C6FF00]">Recommended HughLaunch Solution</p>
            <p className="mt-3 text-2xl font-extrabold">{data.plan.name}</p>
            {rx.why_fit && <p className="mt-2 text-sm text-[#c8cdd3]">{rx.why_fit}</p>}
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {[["Timeline", rx.timeline], ["Investment", `$${Number(data.plan.amount).toLocaleString()}`], ["Expected Impact", rx.expected_impact]].map(([k, v]) => v ? (
                <div key={k} className="rounded-xl bg-[#14161b] p-3"><p className="text-[11px] font-bold uppercase text-[#8A939B]">{k}</p><p className="mt-1 text-sm font-semibold text-white">{v}</p></div>
              ) : null)}
            </div>
            {rx.addons && <p className="mt-3 text-sm text-[#8A939B]"><span className="font-bold text-white">Optional add-ons:</span> {rx.addons}</p>}
            {data.won ? (
              <p className="mt-5 flex items-center gap-2 font-extrabold text-[#C6FF00]"><CheckCircle2 className="h-5 w-5" /> Project started — welcome aboard!</p>
            ) : (
              <button data-testid="rx-accept-btn" disabled={busy} onClick={accept}
                className="mt-5 rounded-full bg-[#C6FF00] px-8 py-4 text-base font-extrabold text-[#0A0C0F] transition-transform hover:scale-[1.02] disabled:opacity-50">
                {busy ? "Preparing checkout…" : <>START MY PROJECT <ArrowRight className="ml-1 inline h-5 w-5" /></>}
              </button>
            )}
            {rx.next_step && <p className="mt-3 text-xs text-[#8A939B]">{rx.next_step}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
